import { useLocalStorageState } from "ahooks";
import dayjs from "dayjs";
import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  IChartingLibraryWidget,
  LanguageCode,
  MouseEventParams,
  ResolutionString,
  ThemeName,
  Timezone,
  TradingTerminalFeatureset,
  TradingTerminalWidgetOptions,
  widget,
} from "tradingView";
import { antdPrimaryTheme, colors } from "../initConfig/color";
import { gapNumber, getNearTime } from "../initConfig/datafeed";
import { initDatafeedParams } from "../initConfig/tradeView";
import {
  getLocalTimeZone,
  numFormatter,
  postWindowMessage,
} from "../initConfig/utils";
import { GloablMessageSub, GlobalMessageType, PostMessageType } from "../rxjs";

export type TVChartContainerProps = Partial<initDatafeedParams> & {
  onChartReady?: () => void;
  tvWidgetRef: RefObject<IChartingLibraryWidget | null>;
  widgetProps: TradingTerminalWidgetOptions;
};

export const TVChartContainer = React.memo(
  ({
    onChartReady,
    tvWidgetRef,
    widgetProps,
    themeName = "dark",
    locale = "zh",
    hideBottom,
    hideLeft,
    hideTop,
    symbol,
    chain,
    address,
    upColor = colors.green,
    downColor = colors.red,
    bgColor = colors.dark,
    minTimeGap = 2 * 60 * 60,
    fullscreen,
  }: TVChartContainerProps) => {
    const chartContainerRef = useRef<HTMLDivElement | null>(null);
    // 自定义css
    const cssBlobUrl = useMemo(() => {
      const customCSS = `.theme-dark:root {
  --tv-color-platform-background:${bgColor};
  --tv-color-pane-background: transparent;
  --tv-color-toolbar-button-text-active: ${antdPrimaryTheme.colorTextActive};
  --tv-color-toolbar-button-text-active-hover: ${antdPrimaryTheme.colorTextHover};
  --tv-color-popup-element-background-active: #303030;
  --tv-color-popup-background: #303030;
  --tv-color-popup-element-text-active: ${antdPrimaryTheme.colorTextActive};
  --tv-color-popup-element-text-hover: ${antdPrimaryTheme.colorTextHover};
  --tv-color-toolbar-button-text: #ffffff4d;
  --ui-lib-checkbox-color-accent: ${antdPrimaryTheme.color};
  --tv-color-popup-element-background-hover: ##454545;
}
html.theme-dark {
  .group-MBOVGQRI {
    flex: unset;
  }

  .loading-indicator {
    opacity: 0 !important;
  }
  .item-SqYYy1zF {
    background-color: ${bgColor};
    border-radius: 4px;
  }
  .button-GwQQdU8S.isInteractive-GwQQdU8S.isActive-GwQQdU8S {
    background-color: #1a1a1a;
  }
  .dialog-CPGBbsmc {
    background-color: ${bgColor};
  }
  .opacitySlider-EnWts7Xu {
    color: ${antdPrimaryTheme.color} !important;
  }
  .opacitySliderGradient-EnWts7Xu {
    background-image: linear-gradient(90deg, transparent, ${antdPrimaryTheme.color}) !important;
  }
  .intent-primary-WDZ0PRNh {
    --ui-lib-intent-highlight-color: ${antdPrimaryTheme.color};
    --ui-lib-intent-color: ${antdPrimaryTheme.color}
  }
  .colorPickerWrap-Sw_a4qpB.intent-primary-Sw_a4qpB, .colorPickerWrap-Sw_a4qpB.intent-primary-Sw_a4qpB .shadow-Sw_a4qpB, html.theme-dark .colorPickerWrap-Sw_a4qpB.intent-primary-Sw_a4qpB, html.theme-dark .colorPickerWrap-Sw_a4qpB.intent-primary-Sw_a4qpB .shadow-Sw_a4qpB,
  .swatch-sfn7Lezv.selected-sfn7Lezv:after {
    border-color: ${antdPrimaryTheme.color}
  }
  .thicknessItem-QStmZL8l.checked-QStmZL8l {
    &,
    &:hover {
      background-color: ${antdPrimaryTheme.color};
      border-color: ${antdPrimaryTheme.color};
    }
  }
  .color-brand-D4RPB3ZC.variant-primary-D4RPB3ZC {
    --ui-lib-button-default-color-border: ${antdPrimaryTheme.color};
    --ui-lib-button-default-color-bg: ${antdPrimaryTheme.color};

    &:hover {
      --ui-lib-button-default-color-border: ${antdPrimaryTheme.colorHover};
      --ui-lib-button-default-color-bg: ${antdPrimaryTheme.colorHover};
    }
  }
  .color-brand-D4RPB3ZC.variant-secondary-D4RPB3ZC {
    --ui-lib-button-default-color-content: ${antdPrimaryTheme.color};
    --ui-lib-button-default-color-border: ${antdPrimaryTheme.color};
    --ui-lib-button-default-color-bg: transparent;
    
    &:hover {
      --ui-lib-button-default-color-border: ${antdPrimaryTheme.colorHover};
      --ui-lib-button-default-color-bg: ${antdPrimaryTheme.colorHover};
      --ui-lib-button-default-color-content: #ffffff;
    }
  }
  .text-hF57_4zZ{
    white-space: pre-line;
    text-align: left;
  }
  .price-change-button {
    &.price {
      .price {
        color: #fff;
      }
    }

    &.mcp {
      .mcp {
        color: #fff;
      }
    }
  }
}
`;

      const cssBlob = new Blob([customCSS], {
        type: "text/css",
      });
      return URL.createObjectURL(cssBlob);
    }, []);

    const [showMarketStorage = true, setShowMarketStorage] =
      useLocalStorageState<boolean>("showMarket");

    const [showMarket, setShowMarket] = useState(showMarketStorage);
    const [supply, setSupply] = useState(1000000000);
    const marketButtonRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
      const subp = GloablMessageSub.subscribe(({ type, data }) => {
        if (type === GlobalMessageType.GET_TOKEN_INFO) {
          setSupply(Number(data.result.supply));
          subp.unsubscribe();
        }
      });

      return () => {
        subp?.unsubscribe();
      };
    }, []);

    const initTvWidget = useCallback(() => {
      if (!chartContainerRef.current) return;
      if (!widgetProps.datafeed) return;

      const disabled_features: TradingTerminalFeatureset[] = [
        "use_localstorage_for_settings",
        "chart_crosshair_menu",
        "order_panel",
        "show_object_tree",
        "symbol_info",
        "compare_symbol_search_spread_operators",
        "header_symbol_search",
        "header_compare",
        "snapshot_trading_drawings",
        "right_toolbar",
        "header_screenshot", // 截屏
        "header_saveload",
        "header_undo_redo",
        "show_interval_dialog_on_key_press",
        "go_to_date",
        "scales_date_format",
        "popup_hints",
        "display_market_status",
        "trading_account_manager",
        "header_indicators",
        "volume_force_overlay", // 禁用成交量强制覆盖
        // "create_volume_indicator_by_default", // 禁用默认创建成交量指标
      ];
      if (hideLeft === "true") {
        disabled_features.push("left_toolbar");
      }
      if (hideBottom === "true") {
        disabled_features.push("timeframes_toolbar");
      }
      if (hideTop === "true") {
      }
      console.log("fullscreen", fullscreen);
      const widgetOptions: TradingTerminalWidgetOptions = {
        // debug: true,
        symbol: symbol || " ",
        theme: themeName as ThemeName,
        timezone: getLocalTimeZone() as Timezone,
        datafeed: widgetProps.datafeed,
        interval: widgetProps.interval as ResolutionString,
        container: chartContainerRef.current,
        library_path: widgetProps.library_path,
        locale: locale as LanguageCode,
        charts_storage_url: widgetProps.charts_storage_url,
        charts_storage_api_version: widgetProps.charts_storage_api_version,
        client_id: widgetProps.client_id,
        user_id: widgetProps.user_id,
        fullscreen: fullscreen === "true",
        autosize: widgetProps.autosize,
        disabled_features,
        enabled_features: [
          "header_resolutions",
          "save_chart_properties_to_local_storage",
          "two_character_bar_marks_labels",
          "seconds_resolution",
          "disable_resolution_rebuild",
          "hide_price_scale_if_all_sources_hidden",
        ],
        toolbar_bg:
          themeName === "dark"
            ? bgColor || colors.dark
            : bgColor || colors.white,
        // demo里面如果有false的一定要小心看，避免一直找怎么打开已经关闭的功能
        overrides: {
          "paneProperties.backgroundType": "solid",
          "paneProperties.legendProperties.showLegend": true,
          "paneProperties.background":
            themeName === "dark"
              ? bgColor || colors.dark
              : bgColor || colors.white,
          "mainSeriesProperties.candleStyle.upColor": upColor,
          "mainSeriesProperties.candleStyle.downColor": downColor,
          "mainSeriesProperties.candleStyle.drawBorder": false,
          "mainSeriesProperties.candleStyle.borderUpColor": upColor,
          "mainSeriesProperties.candleStyle.borderDownColor": downColor,
          "mainSeriesProperties.candleStyle.wickUpColor": upColor,
          "mainSeriesProperties.candleStyle.wickDownColor": downColor,
          // 隐藏成交量相关配置
          volumePaneSize: "small",
          "paneProperties.topMargin": 10,
          "paneProperties.bottomMargin": 10,
          ...widgetProps.overrides,
        },
        studies_overrides: {
          "volume.volume.color.1": upColor,
          "volume.volume.color.0": downColor,
          "volume.volume.linewidth": 1,
          ...widgetProps.studies_overrides,
        },
        custom_css_url: cssBlobUrl,
        time_frames: widgetProps.time_frames || [],
        custom_formatters: {
          // 价格格式化
          priceFormatterFactory: () => ({
            format: (price: number) =>
              showMarket
                ? numFormatter(Math.max(0, price ?? 0) * supply, 2)
                : numFormatter(Math.max(0, price ?? 0), 4),
          }),
          // 成交量格式化
          studyFormatterFactory: () => ({
            format: (price: number) => numFormatter(price, 2),
          }),
        } as any,
      };

      const tvWidget = new widget(widgetOptions);
      tvWidgetRef.current = tvWidget;
      (window as any).tvWidgetRef = tvWidgetRef.current;

      tvWidget.onChartReady(() => {
        if (onChartReady) {
          onChartReady();
        }
        const activeChart = tvWidget.activeChart();
        const range = activeChart.getVisibleRange();
        const minFrom = dayjs().unix() - minTimeGap;
        activeChart.setVisibleRange(
          { from: Math.min(minFrom, range.from), to: range.to },
          {
            percentRightMargin: 25,
          }
        );
        // f(x) 添加指标
        tvWidget.headerReady().then(() => {
          tvWidget.subscribe("mouse_down", onMouseDown);
          tvWidget.subscribe("mouse_up", onMouseUp);

          subscribeChartsChanges();
          const marketButton = tvWidget.createButton();
          marketButtonRef.current = marketButton;
          marketButton.innerHTML =
            "<span class='price'>Price</span>/<span class='mcp'>MCP</span>";
          marketButton.classList.add("price-change-button");
          marketButton.classList.add(showMarket ? "mcp" : "price");
          marketButton.setAttribute("title", "Change Price/Market");
          marketButton.addEventListener("click", () => {
            setShowMarket((pre) => !pre);
          });
        });
      });
    }, []);

    useEffect(() => {
      initTvWidget();

      return () => {
        // tvWidgetRef.current?.remove();
      };
    }, []);

    useEffect(() => {
      setShowMarketStorage(showMarket);
      try {
        if (tvWidgetRef.current?.activeChart) {
          const activeChart = tvWidgetRef.current.activeChart();
          if (activeChart) {
            activeChart.priceFormatter().format = showMarket
              ? (price) => numFormatter(Math.max(0, price ?? 0) * supply, 2)
              : (price) => numFormatter(Math.max(0, price ?? 0), 4);
            (activeChart.getSeries() as any).priceScale().setMode(1);
            (activeChart.getSeries() as any).priceScale().setMode(0);
            if (marketButtonRef.current) {
              marketButtonRef.current.classList.remove(
                showMarket ? "price" : "mcp"
              );
              marketButtonRef.current.classList.add(
                showMarket ? "mcp" : "price"
              );
            }
          }
        }
      } catch {}
    }, [showMarket, supply]);

    const mouseDownParamsRef = useRef<MouseEventParams | null>(null);
    const movePeriodRef = useRef<{
      from: number;
      resolution: ResolutionString;
    } | null>(null);

    const onMouseUp = useCallback((e: MouseEventParams) => {
      if (
        mouseDownParamsRef.current?.clientX !== e.clientX ||
        mouseDownParamsRef.current.clientY !== e.clientY
      ) {
        mouseDownParamsRef.current = null;
        return;
      }
      if (movePeriodRef.current?.from) {
        const range = {
          from: movePeriodRef.current?.from,
          to:
            movePeriodRef.current?.from +
            gapNumber[movePeriodRef.current?.resolution || "1"] * 60,
        };
        postWindowMessage({ type: PostMessageType.PERIOD_CLICK, data: range });
      }
    }, []);
    const onMouseDown = useCallback((e: MouseEventParams) => {
      if (tvWidgetRef.current?.selectedLineTool() === "cursor") {
        mouseDownParamsRef.current = e;
      }
    }, []);

    const subscribeChartsChanges = useCallback(() => {
      const tvWidget = tvWidgetRef.current;
      if (tvWidget) {
        const chart = tvWidget.activeChart();
        const crossHairMoved = chart.crossHairMoved();
        crossHairMoved.subscribe(null, (e) => {
          movePeriodRef.current = {
            from: Math.min(
              dayjs(
                getNearTime(dayjs().unix(), gapNumber[chart.resolution()])
              ).unix(),
              e.time
            ),
            resolution: chart.resolution(),
          };
        });
      }
    }, []);

    return (
      <div
        style={{
          position: "relative",
          height: "100%",
          width: "100%",
        }}
      >
        <div
          ref={chartContainerRef}
          style={{
            width: "100%",
            height: "100%",
          }}
        />
      </div>
    );
  }
);
